import { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "@heroui/react";
import {
  api,
  ShoppingItem,
  ShoppingHistoryEntry,
  ShoppingHistoryResponse,
} from "../api/client";
import {
  formatUnit,
  SHOP_ZONE_LABELS,
  SHOP_ZONE_UNASSIGNED_LABEL,
  shopZoneRank,
} from "../constants";
import { IngredientKindIcon } from "../components/IngredientKindIcon";
import { parseShoppingInput } from "../utils/shoppingParser";

const DELETE_GRACE_MS = 5000;
const LIVE_REFRESH_MS = 6000;
const NEW_ROW_ANIMATION_MS = 1800;

export function ShoppingList() {
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearingItems, setClearingItems] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  const pendingDeletes = useRef<ShoppingItem[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const knownItemIds = useRef<Set<number>>(new Set());
  const initializedLiveUpdates = useRef(false);
  const newRowTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const nextOptimisticId = useRef(-1);
  const optimisticAdds = useRef<Map<number, ShoppingItem>>(new Map());
  const removedOptimisticAdds = useRef<Set<number>>(new Set());
  const [newItemIds, setNewItemIds] = useState<number[]>([]);

  const markNewItems = useCallback((ids: number[]) => {
    if (!ids.length) return;
    setNewItemIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return [...next];
    });
    ids.forEach((id) => {
      const existing = newRowTimers.current.get(id);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        newRowTimers.current.delete(id);
        setNewItemIds((prev) => prev.filter((currentId) => currentId !== id));
      }, NEW_ROW_ANIMATION_MS);
      newRowTimers.current.set(id, timer);
    });
  }, []);

  const load = useCallback(async () => {
    const data = await api.get<ShoppingItem[]>("/shopping");
    const pendingDeleteIds = new Set(pendingDeletes.current.map((item) => item.id));
    const serverItems = data.filter((item) => !pendingDeleteIds.has(item.id));
    const optimisticDrafts = [...optimisticAdds.current.values()];
    const optimisticItems = optimisticDrafts.filter(
      (optimisticItem) =>
        !pendingDeleteIds.has(optimisticItem.id) &&
        !serverItems.some((serverItem) => isLikelySameShoppingItem(serverItem, optimisticItem))
    );
    const sorted = sortItems([...serverItems, ...optimisticItems]);
    if (initializedLiveUpdates.current) {
      const remoteNewIds = sorted
        .filter(
          (item) =>
            item.id > 0 &&
            !knownItemIds.current.has(item.id) &&
            !optimisticDrafts.some((optimisticItem) => isLikelySameShoppingItem(item, optimisticItem))
        )
        .map((item) => item.id);
      markNewItems(remoteNewIds);
    }
    knownItemIds.current = new Set(sorted.filter((item) => item.id > 0).map((item) => item.id));
    initializedLiveUpdates.current = true;
    setItems(sorted);
    setLoading(false);
  }, [markNewItems]);

  useEffect(() => {
    load().catch((error) => {
      console.error("No s'ha pogut carregar la llista de la compra", error);
      setLoading(false);
    });
  }, [load]);

  useEffect(() => {
    function onChanged() {
      load().catch((error) => {
        console.error("No s'ha pogut refrescar la llista de la compra", error);
      });
    }
    window.addEventListener("cookmenu:data-changed", onChanged);
    return () => window.removeEventListener("cookmenu:data-changed", onChanged);
  }, [load]);

  useEffect(() => {
    const refreshIfOnline = () => {
      if (document.visibilityState !== "visible" || !window.navigator.onLine) return;
      load().catch((error) => {
        console.error("No s'ha pogut actualitzar la llista en temps real", error);
      });
    };
    const intervalId = setInterval(refreshIfOnline, LIVE_REFRESH_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshIfOnline();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", refreshIfOnline);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", refreshIfOnline);
    };
  }, [load]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setName("");
    const parsed = parseShoppingInput(trimmed);
    const optimisticItem: ShoppingItem = {
      id: nextOptimisticId.current--,
      name: parsed.name || trimmed.toLowerCase(),
      quantity: parsed.quantity,
      unit: parsed.unit,
      ingredientId: null,
      checked: false,
      position: Math.max(0, ...items.map((item) => item.position)) + 1,
      createdAt: new Date().toISOString(),
      shopZone: null,
      packaged: false,
    };
    optimisticAdds.current.set(optimisticItem.id, optimisticItem);
    setItems((prev) => sortItems([...prev, optimisticItem]));

    try {
      const item = await api.post<ShoppingItem>("/shopping", {
        name: parsed.name || trimmed.toLowerCase(),
        quantity: parsed.quantity,
        unit: parsed.unit,
      });
      optimisticAdds.current.delete(optimisticItem.id);
      knownItemIds.current.add(item.id);
      if (removedOptimisticAdds.current.has(optimisticItem.id)) {
        removedOptimisticAdds.current.delete(optimisticItem.id);
        return;
      }
      setItems((prev) => {
        const withoutOptimistic = prev.filter((i) => i.id !== optimisticItem.id);
        const exists = withoutOptimistic.some((i) => i.id === item.id);
        const next = exists
          ? withoutOptimistic.map((i) => (i.id === item.id ? item : i))
          : [...withoutOptimistic, item];
        return sortItems(next);
      });
    } catch (error) {
      optimisticAdds.current.delete(optimisticItem.id);
      removedOptimisticAdds.current.delete(optimisticItem.id);
      setItems((prev) => prev.filter((item) => item.id !== optimisticItem.id));
      setName(trimmed);
      console.error("No s'ha pogut afegir l'article a la llista de la compra", error);
    }
  }

  async function toggle(item: ShoppingItem) {
    const next = !item.checked;
    setItems((prev) => sortItems(prev.map((i) => (i.id === item.id ? { ...i, checked: next } : i))));
    try {
      await api.patch(`/shopping/${item.id}`, { checked: next });
    } catch {
      load().catch((error) => {
        console.error("No s'ha pogut recuperar l'estat de la llista de la compra", error);
      });
    }
  }

  function scheduleFlush() {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushDeletes, DELETE_GRACE_MS);
  }

  function flushDeletes() {
    if (flushTimer.current) { clearTimeout(flushTimer.current); flushTimer.current = null; }
    const toDelete = pendingDeletes.current;
    pendingDeletes.current = [];
    setPendingCount(0);
    toDelete.forEach((it) => {
      api.del(`/shopping/${it.id}`).catch(() => {
        load().catch((error) => {
          console.error("No s'ha pogut recuperar la llista després d'esborrar", error);
        });
      });
    });
  }

  function undoDeletes() {
    if (flushTimer.current) { clearTimeout(flushTimer.current); flushTimer.current = null; }
    const restored = pendingDeletes.current;
    pendingDeletes.current = [];
    setPendingCount(0);
    restored.forEach((item) => knownItemIds.current.add(item.id));
    setItems((cur) => sortItems([...cur, ...restored]));
  }

  function remove(item: ShoppingItem) {
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    if (item.id < 0) {
      optimisticAdds.current.delete(item.id);
      removedOptimisticAdds.current.add(item.id);
      return;
    }
    knownItemIds.current.delete(item.id);
    pendingDeletes.current = [...pendingDeletes.current, item];
    setPendingCount(pendingDeletes.current.length);
    scheduleFlush();
  }

  useEffect(() => {
    return () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
      pendingDeletes.current.forEach((it) => api.del(`/shopping/${it.id}`).catch(() => {}));
      newRowTimers.current.forEach((timer) => clearTimeout(timer));
      newRowTimers.current.clear();
      optimisticAdds.current.clear();
      removedOptimisticAdds.current.clear();
    };
  }, []);

  async function clearChecked() {
    setClearingItems(true);
    try {
      setItems((prev) => prev.filter((i) => !i.checked));
      knownItemIds.current = new Set(items.filter((i) => !i.checked).map((i) => i.id));
      await api.del("/shopping");
      setHistoryVersion((v) => v + 1);
      setShowClearModal(false);
    } catch {
      load().catch((error) => {
        console.error("No s'ha pogut recuperar la llista després de guardar la compra", error);
      });
    } finally {
      setClearingItems(false);
    }
  }

  const pending = items.filter((i) => !i.checked);
  const done = items.filter((i) => i.checked);
  const pendingGroups = groupByZone(pending);
  const parsed = parseShoppingInput(name);
  const preview = parsed.name || parsed.quantity !== null || parsed.unit !== null;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Llista de la compra</h1>
          <div className="sub">{pending.length} per comprar · {done.length} a la cistella</div>
        </div>
      </div>

      <div className="sh-grid">
        {/* Columna principal */}
        <div>
          {/* Formulari d'afegir */}
          <form onSubmit={add} className="flex flex-col gap-1.5 mb-5">
            <div className="flex gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Afegeix un article… (ex: 2 ous, 1kg patata)"
                aria-label="Article nou"
                className="nm-input flex-1"
              />
              <button
                type="submit"
                disabled={!name.trim()}
                className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Afegeix
              </button>
            </div>
            {preview && (
              <div className="text-xs text-muted px-1">
                <span className="font-semibold">
                  {parsed.quantity ? `${parsed.quantity} ${parsed.unit || ""}` : ""}
                  {parsed.quantity && parsed.name ? " " : ""}
                  {parsed.name}
                </span>
              </div>
            )}
          </form>

          {loading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : items.length === 0 ? (
            <div className="empty" style={{ minHeight: 140 }}>
              <b>La llista és buida</b>
              <span className="muted">Afegeix articles o fes servir la icona 🛒 a les receptes</span>
            </div>
          ) : (
            <>
              {/* Per comprar */}
              {pending.length > 0 && (
                <div className="mb-5">
                  <div className="section-title mb-2.5">Per comprar · {pending.length}</div>
                  <div className="flex flex-col gap-4">
                    {pendingGroups.map((group) => (
                      <div key={group.key || "_none"}>
                        <div className="sh-zone-title">
                          {group.label} · {group.items.length}
                        </div>
                        <div className="card sh-card" style={{ overflow: "hidden" }}>
                          {group.items.map((item, i) => (
                            <div key={item.id} style={{ borderTop: i ? "1px solid var(--border)" : 0 }}>
                              <ItemRow
                                item={item}
                                onToggle={toggle}
                                onRemove={remove}
                                isNew={newItemIds.includes(item.id)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* A la cistella */}
              {done.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="section-title" style={{ color: "var(--success-soft-foreground)" }}>A la cistella · {done.length}</div>
                    <button
                      type="button"
                      onClick={() => setShowClearModal(true)}
                      className="rounded-lg bg-success-soft px-3 py-1.5 text-xs font-bold text-success-soft-foreground transition-opacity hover:opacity-80"
                    >
                      Compra feta
                    </button>
                  </div>
                  <div className="card sh-card sh-cart" style={{ overflow: "hidden" }}>
                    {done.map((item, i) => (
                      <div key={item.id} style={{ borderTop: i ? `1px solid color-mix(in oklab, var(--success) 18%, var(--border))` : 0 }}>
                        <ItemRow
                          item={item}
                          onToggle={toggle}
                          onRemove={remove}
                          isNew={newItemIds.includes(item.id)}
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowClearModal(true)}
                    className="mt-3 w-full rounded-xl bg-success px-4 py-3 text-sm font-bold text-success-foreground transition-opacity hover:opacity-90"
                  >
                    Finalitza la compra ({done.length})
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar: històric */}
        <aside>
          <HistoryPanel version={historyVersion} />
        </aside>
      </div>

      {/* Snackbar desfés */}
      {pendingCount > 0 && (
        <div className="snackbar-bottom fixed inset-x-0 z-50 flex justify-center px-4">
          <div className="flex items-center gap-3 rounded-full border border-border bg-surface px-4 py-2 text-sm shadow-card-md">
            <span className="text-foreground">
              {pendingCount} article{pendingCount !== 1 ? "s" : ""} esborrat{pendingCount !== 1 ? "s" : ""}
            </span>
            <button type="button" onClick={undoDeletes} className="font-bold text-accent hover:underline">
              Desfés
            </button>
          </div>
        </div>
      )}

      {/* Modal confirmació compra */}
      {showClearModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(25,18,12,.42)", animation: "fade-in .15s ease" }}
          onClick={() => setShowClearModal(false)}
        >
          <div
            className="card w-full max-w-sm"
            style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 16 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 style={{ fontSize: 18, fontFamily: "var(--font-head)", fontWeight: 600 }}>
                Guardar compra ({done.length} article{done.length !== 1 ? "s" : ""})
              </h2>
              <p className="text-sm text-muted mt-1.5">
                Es guardarà com a històric i els articles marcats s'esborraran de la llista actual.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowClearModal(false)}
                disabled={clearingItems}
                className="rounded-xl border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-surface-secondary disabled:opacity-50"
              >
                Cancel·lar
              </button>
              <button
                type="button"
                onClick={clearChecked}
                disabled={clearingItems}
                className="rounded-xl bg-success px-4 py-2 text-sm font-bold text-success-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {clearingItems ? (
                  <span className="flex items-center gap-2"><Spinner size="sm" />Guardant…</span>
                ) : (
                  "Compra feta"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemRow({
  item,
  onToggle,
  onRemove,
  isNew,
}: {
  item: ShoppingItem;
  onToggle: (i: ShoppingItem) => void;
  onRemove: (i: ShoppingItem) => void;
  isNew?: boolean;
}) {
  const qty =
    item.quantity != null
      ? `${formatQty(item.quantity)} ${item.unit ? formatUnit(item.unit, item.quantity) : ""}`.trim()
      : item.unit ?? "";

  return (
    <label className={`sh-row ${item.checked ? "done" : ""} ${isNew ? "new" : ""}`}>
      <input type="checkbox" checked={item.checked} onChange={() => onToggle(item)} style={{ display: "none" }} />
      <span className={`sh-check ${item.checked ? "on" : ""}`}>
        {item.checked && (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <IngredientKindIcon packaged={!!item.packaged} style={{ marginTop: 1 }} />
      <span className="sh-name">
        {qty && (
          <em style={{ fontStyle: "normal", fontWeight: 400, color: "var(--muted)", fontSize: 13, marginRight: 6 }}>
            {qty}
          </em>
        )}
        {item.name}
      </span>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); onRemove(item); }}
        aria-label="Esborra"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-danger-soft hover:text-danger"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
        </svg>
      </button>
    </label>
  );
}

function HistoryPanel({ version }: { version: number }) {
  const PAGE = 10;
  const [entries, setEntries] = useState<ShoppingHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  async function loadPage(skip: number, replace: boolean) {
    const res = await api.get<ShoppingHistoryResponse>(`/shopping/history?skip=${skip}&take=${PAGE}`);
    setTotal(res.total);
    setEntries((prev) => (replace ? res.entries : [...prev, ...res.entries]));
  }

  useEffect(() => {
    setLoading(true);
    loadPage(0, true).finally(() => setLoading(false));
  }, [version]);

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function loadMore() {
    setLoadingMore(true);
    await loadPage(entries.length, false).finally(() => setLoadingMore(false));
  }

  return (
    <div>
      <div className="section-title mb-3">Històric de compres</div>

      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted py-8 text-center">
          Encara no hi ha compres guardades.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {entries.map((h) => {
            const isOpen = expanded.has(h.id);
            return (
              <div key={h.id} className="card" style={{ overflow: "hidden" }}>
                <button
                  type="button"
                  onClick={() => toggleExpand(h.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-secondary"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-bold">{formatHistoryDate(h.savedAt)}</div>
                    <div className="text-xs text-muted mt-0.5">{h.itemCount} article{h.itemCount !== 1 ? "s" : ""}</div>
                  </div>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: "var(--faint)", flexShrink: 0, transition: ".15s", transform: isOpen ? "rotate(90deg)" : "none" }}
                    aria-hidden
                  >
                    <polyline points="9 6 15 12 9 18" />
                  </svg>
                </button>
                {isOpen && (
                  <ul className="flex flex-col gap-1 border-t border-border px-4 py-3">
                    {h.items.map((it, idx) => {
                      const qty =
                        it.quantity != null
                          ? `${formatQty(it.quantity)} ${it.unit ? formatUnit(it.unit, it.quantity) : ""}`.trim()
                          : it.unit ?? "";
                      return (
                        <li key={`${h.id}-${idx}`} className="flex items-baseline justify-between gap-2 text-sm">
                          <span>{it.name}</span>
                          {qty && <span className="text-xs text-muted">{qty}</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && entries.length < total && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className="mt-3 w-full rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-muted transition-colors hover:bg-surface-secondary disabled:opacity-50"
        >
          {loadingMore ? "Carregant…" : "Carrega'n més"}
        </button>
      )}
    </div>
  );
}

type ZoneGroup = { key: string; label: string; items: ShoppingItem[] };

/** Agrupa per categoria de supermercat, ordre de botiga i alfabètic dins el grup. */
function groupByZone(list: ShoppingItem[]): ZoneGroup[] {
  const map = new Map<string, ShoppingItem[]>();
  for (const it of list) {
    const key = it.shopZone || "";
    const bucket = map.get(key);
    if (bucket) bucket.push(it);
    else map.set(key, [it]);
  }
  const groups: ZoneGroup[] = [...map.entries()].map(([key, gItems]) => ({
    key,
    label: key ? SHOP_ZONE_LABELS[key] ?? key : SHOP_ZONE_UNASSIGNED_LABEL,
    items: [...gItems].sort((a, b) => a.name.localeCompare(b.name, "ca")),
  }));
  return groups.sort(
    (a, b) =>
      shopZoneRank(a.key || null) - shopZoneRank(b.key || null) ||
      a.label.localeCompare(b.label, "ca")
  );
}

function sortItems(list: ShoppingItem[]): ShoppingItem[] {
  return [...list].sort((a, b) => {
    if (a.checked !== b.checked) return a.checked ? 1 : -1;
    return a.position - b.position;
  });
}

function isLikelySameShoppingItem(a: ShoppingItem, b: ShoppingItem): boolean {
  return (
    a.name.trim().toLocaleLowerCase("ca") === b.name.trim().toLocaleLowerCase("ca") &&
    (a.unit ?? "") === (b.unit ?? "")
  );
}

function formatHistoryDate(iso: string): string {
  return new Date(iso).toLocaleString("ca-ES", {
    timeZone: "Europe/Madrid",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatQty(q: number): string {
  if (Number.isInteger(q)) return String(q);
  return (Math.round(q * 100) / 100).toString();
}
